import type { ReactNode } from 'react';

import { Badge } from '../../../components/ui';
import { useT } from '../../../i18n';
import { KHONG_CO, soVN } from '../../../lib/dinh-dang';
import { TRANG_THAI_LICENSE } from '../constants';
import type { Entitlements, TrangThaiLicense } from '../types';

export function BadgeTrangThaiLicense({ trangThai }: { trangThai: TrangThaiLicense }) {
  const { t } = useT();
  const muc = TRANG_THAI_LICENSE[trangThai];
  return <Badge variant={muc.variant}>{t(muc.khoa)}</Badge>;
}

/** Một dòng nhãn — giá trị, dùng cho tóm tắt trong hộp thoại và bước Xem lại. */
export function DongThuocTinh({ nhan, children }: { nhan: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-default py-2 last:border-0 sm:flex-row sm:gap-3">
      <span className="shrink-0 text-xs text-content-muted sm:w-44">{nhan}</span>
      <span className="min-w-0 flex-1 text-sm text-content-primary">{children}</span>
    </div>
  );
}

/** Mã dài (UUID, digest) — chữ đơn cách, cho xuống dòng thay vì đẩy tràn khung. */
export function MaDai({ children }: { children: ReactNode }) {
  return <span className="break-all font-mono text-xs text-content-secondary">{children}</span>;
}

/**
 * Tóm tắt `entitlements`. Module và feature là *loại*, không phải mức độ, nên
 * dùng thang phân loại (`info`, `violet`) — không phải `success`.
 */
export function TomTatNoiDung({ noiDung }: { noiDung: Entitlements }) {
  const { t, locale } = useT();
  const gioiHan = Object.entries(noiDung.limits);
  return (
    <div>
      <DongThuocTinh nhan={t('license.oModules')}>
        {noiDung.modules.length === 0 ? (
          <span className="text-content-muted">{KHONG_CO}</span>
        ) : (
          <span className="flex flex-wrap gap-1">
            {noiDung.modules.map((m) => (
              <Badge key={m} variant="info">
                <span className="font-mono">{m}</span>
              </Badge>
            ))}
          </span>
        )}
      </DongThuocTinh>
      <DongThuocTinh nhan={t('license.oMaxUsers')}>
        <span className="tabular-nums">{soVN(noiDung.max_users, locale)}</span>
      </DongThuocTinh>
      <DongThuocTinh nhan={t('license.oMaxNodes')}>
        <span className="tabular-nums">{soVN(noiDung.max_nodes, locale)}</span>
      </DongThuocTinh>
      {gioiHan.map(([ten, gt]) => (
        <DongThuocTinh key={ten} nhan={t('license.oGioiHan', { ten })}>
          <span className="tabular-nums">{soVN(gt, locale)}</span>
        </DongThuocTinh>
      ))}
      <DongThuocTinh nhan={t('license.oFeatures')}>
        {noiDung.features.length === 0 ? (
          <span className="text-content-muted">{t('license.khongCoFeature')}</span>
        ) : (
          <span className="flex flex-wrap gap-1">
            {noiDung.features.map((f) => (
              <Badge key={f} variant="violet">
                <span className="font-mono">{f}</span>
              </Badge>
            ))}
          </span>
        )}
      </DongThuocTinh>
    </div>
  );
}
